import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { auth } from '../firebase';
import { useForm } from 'react-hook-form';
import { AuthLayout } from '../components/auth';
import { FormField, Button } from '../components/forms';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const oobCode = searchParams.get('oobCode');

  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVerifyingCode, setIsVerifyingCode] = useState(Boolean(oobCode));
  const [hasInvalidCode, setHasInvalidCode] = useState(false);

  const { register, handleSubmit, watch, formState: { errors } } = useForm();

  useEffect(() => {
    if (!oobCode) {
      setIsVerifyingCode(false);
      return;
    }

    let isMounted = true;

    const validateResetCode = async () => {
      try {
        await verifyPasswordResetCode(auth, oobCode);
        if (isMounted) {
          setHasInvalidCode(false);
        }
      } catch (err) {
        console.error('Reset link verification error:', err);
        if (isMounted) {
          setHasInvalidCode(true);
          setError('This reset link is invalid or has expired. Please request a new one.');
        }
      } finally {
        if (isMounted) {
          setIsVerifyingCode(false);
        }
      }
    };

    validateResetCode();

    return () => {
      isMounted = false;
    };
  }, [oobCode]);

  const handleResetPassword = async (data) => {
    setError('');
    setIsSubmitting(true);

    try {
      await confirmPasswordReset(auth, oobCode, data.newPassword);
      setSuccess(true);
    } catch (err) {
      console.error('Password reset error:', err);
      if (err.code === 'auth/invalid-action-code') {
        setError('This reset link is invalid or has already been used. Please request a new one.');
      } else if (err.code === 'auth/expired-action-code') {
        setError('This reset link has expired. Please request a new one.');
      } else if (err.code === 'auth/weak-password') {
        setError('Password is too weak. Please use at least 6 characters.');
      } else {
        setError('Failed to reset password. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Missing oobCode in URL
  if (!oobCode) {
    return (
      <AuthLayout title="Invalid Reset Link">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          <p className="text-sm">This reset link is invalid. Please request a new password reset.</p>
          <Link
            to="/forgot-password"
            className="mt-3 inline-block text-sm font-medium text-red-600 hover:text-red-500 underline"
          >
            Request new reset link
          </Link>
        </div>
      </AuthLayout>
    );
  }

  if (isVerifyingCode) {
    return (
      <AuthLayout title="Validating Reset Link">
        <p className="text-sm text-gray-600">Please wait while we verify your reset link.</p>
      </AuthLayout>
    );
  }

  if (hasInvalidCode) {
    return (
      <AuthLayout title="Invalid Reset Link">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          <p className="text-sm">{error || 'This reset link is invalid. Please request a new password reset.'}</p>
          <Link
            to="/forgot-password"
            className="mt-3 inline-block text-sm font-medium text-red-600 hover:text-red-500 underline"
          >
            Request new reset link
          </Link>
        </div>
      </AuthLayout>
    );
  }

  // Success state
  if (success) {
    return (
      <AuthLayout title="Password Reset">
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
          <p className="text-sm font-medium">Your password has been reset successfully.</p>
          <Link
            to="/login"
            className="mt-3 inline-block text-sm font-medium text-green-600 hover:text-green-500 underline"
          >
            Sign in with your new password
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Set New Password"
      subtitle="Enter your new password below."
    >
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start">
          <svg className="h-5 w-5 text-red-400 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          <div>
            <span className="text-sm">{error}</span>
            {(error.includes('invalid') || error.includes('expired')) && (
              <div className="mt-2">
                <Link
                  to="/forgot-password"
                  className="text-sm font-medium text-red-600 hover:text-red-500 underline"
                >
                  Request a new reset link
                </Link>
              </div>
            )}
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit(handleResetPassword)} className="space-y-6">
        <FormField
          label="New Password"
          name="newPassword"
          type="password"
          register={register}
          validation={{
            required: 'Password is required',
            minLength: {
              value: 6,
              message: 'Password must be at least 6 characters',
            },
          }}
          error={errors.newPassword}
          placeholder="Enter new password"
          required
        />

        <FormField
          label="Confirm Password"
          name="confirmPassword"
          type="password"
          register={register}
          validation={{
            required: 'Please confirm your password',
            validate: (value) =>
              value === watch('newPassword') || 'Passwords do not match',
          }}
          error={errors.confirmPassword}
          placeholder="Confirm new password"
          required
        />

        <Button type="submit" loading={isSubmitting} className="w-full">
          {isSubmitting ? 'Resetting...' : 'Reset Password'}
        </Button>
      </form>
    </AuthLayout>
  );
};

export default ResetPasswordPage;
